import { Box, styled } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import * as React from 'react';
import type { JSX } from 'react';

import { bodySmall, bodySmallSemiBold, colorSystem } from '../../../../theme';
import { dateFnsAdapterFor, type DateLocale } from './localization';

export interface CalendarProps {
    selected: Date | null;
    onMonthChange: (m: Date) => void;
    onSelect: (d: Date) => void;
    minDate?: Date;
    maxDate?: Date;
    locale: DateLocale;
}

/** MUI X keeps its own month; no `referenceDate`, which fights `value` and snaps the month back after navigation. */
export function Calendar({ selected, onMonthChange, onSelect, minDate, maxDate, locale }: CalendarProps): JSX.Element {
    const { adapter, dateFnsLocale } = React.useMemo(() => dateFnsAdapterFor(locale), [locale]);

    return (
        <LocalizationProvider dateAdapter={adapter} adapterLocale={dateFnsLocale}>
            <CalendarRoot>
                <DateCalendar
                    value={selected}
                    onChange={(d) => {
                        if (d) onSelect(d);
                    }}
                    onMonthChange={onMonthChange}
                    minDate={minDate}
                    maxDate={maxDate}
                    views={['day']}
                    showDaysOutsideCurrentMonth
                    fixedWeekNumber={6}
                    slotProps={CALENDAR_SLOT_PROPS}
                />
            </CalendarRoot>
        </LocalizationProvider>
    );
}

// The granularity tabs switch year and month, so MUI X's own view switcher is hidden.
const CALENDAR_SLOT_PROPS = {
    calendarHeader: { sx: { '& .MuiPickersCalendarHeader-switchViewButton': { display: 'none' } } },
} as const;

const DAY_ROW_HEIGHT = 36;
const DAY_CELL_HEIGHT = 32;

const CalendarRoot = styled(Box)({
    padding: '4px 4px 8px',
    '& .MuiDateCalendar-root': {
        width: '100%',
        maxHeight: 'unset',
        height: 'auto',
    },
    // Six rows a month (`fixedWeekNumber={6}`, outside days shown), so one fixed height fits every month.
    '& .MuiDayCalendar-slideTransition, & .MuiPickersSlideTransition-root': {
        minHeight: 'unset',
        height: `${6 * DAY_ROW_HEIGHT}px`,
    },
    '& .MuiPickersCalendarHeader-root': {
        padding: '0 4px',
        marginTop: 0,
        marginBottom: '4px',
        minHeight: '32px',
    },
    '& .MuiPickersCalendarHeader-label': {
        ...bodySmallSemiBold,
        color: colorSystem.neutral[8],
    },
    '& .MuiPickersArrowSwitcher-button, & .MuiPickersCalendarHeader-switchViewButton': {
        color: colorSystem.neutral[7],
    },
    // Seven equal columns, so the weekday labels line up with the cells at any popover width.
    '& .MuiDayCalendar-header': {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        padding: 0,
    },
    '& .MuiDayCalendar-weekDayLabel': {
        ...bodySmall,
        color: colorSystem.neutral[6],
        width: 'auto',
        height: '24px',
        margin: 0,
        justifySelf: 'center',
    },
    '& .MuiDayCalendar-weekContainer': {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        padding: 0,
        margin: 0,
    },
    '& .MuiPickerDay-root': {
        ...bodySmall,
        width: '32px',
        height: `${DAY_CELL_HEIGHT}px`,
        margin: 0,
        justifySelf: 'center',
        borderRadius: '6px',
        color: colorSystem.neutral[8],
        '&:hover': {
            background: colorSystem.primary[1],
            color: colorSystem.primary[7],
        },
        '&:focus': {
            background: colorSystem.primary[1],
        },
        '&.Mui-disabled': {
            color: colorSystem.neutral[5],
        },
    },
    '& .MuiPickerDay-today:not(.Mui-selected)': {
        border: `1px solid ${colorSystem.primary[4]}`,
        background: 'transparent',
        color: colorSystem.primary[7],
    },
    '& .MuiPickerDay-root.Mui-selected': {
        background: colorSystem.primary[6],
        color: colorSystem.neutral.white,
        '&:hover, &:focus': {
            background: colorSystem.primary[7],
            color: colorSystem.neutral.white,
        },
    },
    '& .MuiPickerDay-root.MuiPickerDay-dayOutsideMonth': {
        color: colorSystem.neutral[5],
    },
});
